import { CommonTrackingConfig, getTrackingByKey } from '../../../modules/utils/trackingHelper';
import type {
  TierBenefit,
  TierBenefitsData,
  MemberLevelDetail,
  BenefitRow,
  CellValue,
} from '../MemberShipTiersBenefits.types';
import type { RowItems, RowProps, HeaderTitleItems } from '../../AccountTable/AccountTable.types';

const UXL_ICON_MAPPER: Record<string, string> = {
  member_rates: 'member-rates1x',
  ic_more_points_on_stays_10: 'more-points-on-stays',
  all_suite_room: 'all-suites',
  ic_wifi_10: 'wifi',
  ic_member_rates_10: 'member-rates1x',
  ic_keyless_entry_10: 'keyless-entry',
  ic_late_check_out_10: 'late-check-out',
  ic_all_suite_room_10: 'all-suites',
  ic_open_gift_10: 'gift',
  ic_ambassador_10: 'ambassador',
  ic_your_24_10: 'your-24',
};

const ICON_ONLY_CELL_KEYS = new Set(['enhanced_room_upgrade', 'emirates_skywards']);
const CARRY_FORWARD_MODAL_TEXT_KEYS = new Set(['welcome_gift']);

const CTA_COPY_FALLBACK_SOURCE: Record<string, string> = {
  ultimate_reservation_guarantee: 'welcome_gift',
};

export const ORDERED_BENEFIT_KEYS: readonly string[] = [
  'ultimate_reservation_guarantee',
  'bonus_points',
  'late_checkout',
  'welcome_gift',
  'enhanced_room_upgrade',
  'dedicated_elite_support',
  'lounge_access',
  'annual_choice_benefit',
  '48_hour_guarantee',
  'your_24',
  'ambassador_service',
  'ambassador_plus_one',
  'emirates_skywards',
  'hertz_gold_plus_rewards',
  'sixt_status_match',
  'united_mileage_plus',
];

const BENEFIT_ORDER_INDEX: Record<string, number> = ORDERED_BENEFIT_KEYS.reduce<Record<string, number>>(
  (acc, key, index) => {
    acc[key] = index;
    return acc;
  },
  {}
);

// Rank used to sort rows. Listed keys sort by their position in ORDERED_BENEFIT_KEYS;
// unknown keys all share the same max rank and fall back to the service `order` tiebreak.
function getBenefitOrderRank(row: BenefitRow): number {
  const key = row?.customComponentKey ?? '';
  const index = BENEFIT_ORDER_INDEX[key];
  return index === undefined ? Number.MAX_SAFE_INTEGER : index;
}

function getEliteTierNames(memberLevelDetails: MemberLevelDetail[]): string[] {
  return memberLevelDetails?.filter(d => d?.tierName !== 'Basic')?.map(d => d?.tierName);
}

function getAllTierNames(memberLevelDetails: MemberLevelDetail[]): string[] {
  return memberLevelDetails?.map(d => d?.tierName);
}

function isAllMemberBenefit(benefit: TierBenefit, allTierNames: string[]): boolean {
  if (!benefit?.activeTiers) return false;
  if (!allTierNames?.every(t => benefit?.activeTiers?.includes(t))) return false;
  if (benefit?.modalTable && benefit?.modalTable?.length > 0) return false;
  if (benefit?.subtitle) return false;
  return true;
}

function getDedupKey(benefit: TierBenefit): string {
  if (benefit?.customComponentKey) return `CUSTOM::${benefit?.customComponentKey}`;
  return `FALLBACK::${benefit?.title}`;
}

function shouldIgnoreModalTableCells(benefit: TierBenefit): boolean {
  return ICON_ONLY_CELL_KEYS.has(benefit?.customComponentKey ?? '');
}

function shouldCarryForwardModalText(benefit: TierBenefit): boolean {
  return CARRY_FORWARD_MODAL_TEXT_KEYS.has(benefit?.customComponentKey ?? '');
}

function buildBenefitRows(
  tierBenefitsData: TierBenefitsData[],
  memberLevelDetails: MemberLevelDetail[],
  trackingPropertiesInfo?: CommonTrackingConfig[]
): BenefitRow[] {
  if (!tierBenefitsData) return [];
  const tierItems = tierBenefitsData;

  const eliteTierNames = getEliteTierNames(memberLevelDetails);
  const allTierNames = getAllTierNames(memberLevelDetails);

  const benefitMap = new Map<string, BenefitRow>();

  // Pass 1 — seed canonical row metadata from the Basic bucket.
  for (const tierBucket of tierItems) {
    if (tierBucket?.tier !== 'Basic') continue;
    for (const benefit of tierBucket?.benefits ?? []) {
      if (isAllMemberBenefit(benefit, allTierNames)) continue;
      const dedupKey = getDedupKey(benefit);
      if (!benefitMap?.has(dedupKey)) {
        const trackingConfig = trackingPropertiesInfo?.find(t => t.keyValue === benefit.customComponentKey);

        const trackingProperties =
          trackingConfig && benefit.customComponentKey
            ? getTrackingByKey(trackingPropertiesInfo || [], benefit.customComponentKey)
            : undefined;
        const linkType = trackingConfig?.linkType;

        benefitMap?.set(dedupKey, {
          title: benefit?.subtitle ?? benefit?.title,
          description: benefit?.fullText,
          ctaCopy: benefit?.ctaCopy,
          ctaLink: benefit?.ctaLink,
          webCtaLink: benefit?.webCtaLink,
          customComponentKey: benefit?.customComponentKey,
          cells: {},
          order: benefit?.order,
          trackingProperties,
          linkType,
        });
      }
    }
  }

  // Pass 2 — populate per-tier cell values from elite-tier buckets.
  for (const tierBucket of tierItems) {
    const bucketTier = tierBucket?.tier;
    if (!eliteTierNames?.includes(bucketTier)) continue;

    for (const benefit of tierBucket?.benefits ?? []) {
      if (isAllMemberBenefit(benefit, allTierNames)) continue;
      const isOwnedByBucket = benefit?.activeTiers?.includes(bucketTier);
      if (!isOwnedByBucket) continue;

      const dedupKey = getDedupKey(benefit);

      if (!benefitMap?.has(dedupKey)) {
        const trackingConfig = benefit?.customComponentKey
          ? trackingPropertiesInfo?.find(t => t.keyValue === benefit.customComponentKey)
          : undefined;
        const trackingProperties =
          trackingConfig && benefit.customComponentKey
            ? getTrackingByKey(trackingPropertiesInfo || [], benefit.customComponentKey)
            : undefined;
        const linkType = trackingConfig?.linkType;

        benefitMap?.set(dedupKey, {
          title: benefit?.subtitle ?? benefit?.title,
          description: benefit?.fullText,
          ctaCopy: benefit?.ctaCopy,
          ctaLink: benefit?.ctaLink,
          webCtaLink: benefit?.webCtaLink,
          customComponentKey: benefit?.customComponentKey,
          cells: {},
          order: benefit?.order,
          trackingProperties,
          linkType,
        });
      }

      const row = benefitMap.get(dedupKey)!;
      row.order = Math.min(row?.order, benefit?.order);
      const useModalTableCells = !shouldIgnoreModalTableCells(benefit);
      const carryForwardModalText = shouldCarryForwardModalText(benefit);

      // Step 1 — modalTable entries: explicit per-tier labels take priority
      if (useModalTableCells) {
        for (const entry of benefit?.modalTable ?? []) {
          for (const tier of entry?.activeTiers ?? []) {
            if (eliteTierNames?.includes(tier) && !row?.cells?.[tier]) {
              row.cells[tier] = { type: 'text', text: entry?.subtitle };
            }
          }
        }
      }

      // Step 2 — remaining owned tiers fall back to subtitle or checkmark.
      // Use only explicit activeTiers; no fallback to current bucket for customComponentKey
      const ownedTiers: string[] = benefit?.activeTiers ?? [];
      let lastModalText: string | undefined;
      for (const tier of eliteTierNames) {
        if (!ownedTiers?.includes(tier)) continue;

        const existingCell = row?.cells?.[tier];
        if (existingCell?.type === 'text') {
          lastModalText = existingCell?.text;
          continue;
        }

        if (!existingCell) {
          if (carryForwardModalText && lastModalText) {
            row.cells[tier] = { type: 'text', text: lastModalText };
            continue;
          }

          row.cells[tier] = benefit?.subtitle ? { type: 'text', text: benefit?.subtitle } : { type: 'check' };
        }
      }
    }
  }

  const rows = Array.from(benefitMap?.values())?.filter(row => Object.keys(row?.cells)?.length > 0);
  rows?.sort((a, b) => {
    const rankA = getBenefitOrderRank(a);
    const rankB = getBenefitOrderRank(b);
    if (rankA !== rankB) return rankA - rankB;
    // Same rank (e.g. both unknown keys): fall back to the service-provided order.
    return a?.order - b?.order;
  });
  return rows;
}

function cellToRowSubItem(cell: CellValue | undefined, tierIconColor: string): RowProps {
  if (!cell || cell?.type === 'empty') {
    return { subRowText: '', fontIconSubRowComplex: '', fontIconColor: 'default', variation: 'fonticon' };
  }
  if (cell?.type === 'check') {
    return {
      subRowText: '',
      fontIconSubRowComplex: 'icon-check',
      fontIconColor: tierIconColor,
      variation: 'fonticon',
    };
  }
  return { subRowText: cell?.text, fontIconSubRowComplex: '', fontIconColor: 'default', variation: 'text' };
}

export function buildHeaderTitleItems(memberLevelDetails: MemberLevelDetail[], mainTitle: string): HeaderTitleItems[] {
  const eliteDetails = memberLevelDetails?.filter(d => d?.tierName !== 'Basic');
  return [
    {
      mainTitle,
      headerSubItems: eliteDetails?.map(detail => ({
        titleBackGroundColor: detail?.tierHeaderBackground,
        subtitle: detail?.nightsPerYear ?? '',
        title: detail?.name ?? '',
        fontIconDesktopComplex: 'icon-check',
      })),
    },
  ];
}

export function buildAccountTableRowItems(
  tierBenefitsData: TierBenefitsData[],
  memberLevelDetails: MemberLevelDetail[],
  trackingPropertiesInfo?: CommonTrackingConfig[]
): RowItems[] {
  const eliteDetails = memberLevelDetails?.filter(d => d?.tierName !== 'Basic');
  const rows = buildBenefitRows(tierBenefitsData, memberLevelDetails, trackingPropertiesInfo);

  const ctaCopyByKey: Record<string, string | null | undefined> = {};
  for (const row of rows) {
    if (row?.customComponentKey && row?.ctaCopy) {
      ctaCopyByKey[row.customComponentKey] = row.ctaCopy;
    }
  }

  const resolveFallbackCtaCopy = (row: BenefitRow): string | null | undefined => {
    if (row?.ctaCopy) return row.ctaCopy;
    const sourceKey = CTA_COPY_FALLBACK_SOURCE[row?.customComponentKey ?? ''];
    if (sourceKey) {
      return ctaCopyByKey[sourceKey] ?? null;
    }
    return null;
  };

  return rows?.map(row => ({
    rowTitle: row?.title,
    rowSubTitle: row?.description,
    rowCtaCopy: resolveFallbackCtaCopy(row),
    rowCtaLink: row?.webCtaLink ?? row?.ctaLink,
    rowSubItems: eliteDetails?.map(detail =>
      cellToRowSubItem(row?.cells?.[detail?.tierName], detail?.tierIconColor || 'default')
    ),
    trackingProperties: row?.trackingProperties,
    linkType: row?.linkType,
  }));
}

export const getBenefitDisclaimerFromTiers = (
  tiers: TierBenefitsData[] | undefined,
  expectedKey = 'late_checkout'
): string | undefined => {
  if (!tiers?.length) return undefined;

  for (const tier of tiers) {
    for (const benefit of tier.benefits || []) {
      const key = benefit.customComponentKey;
      if (key === expectedKey) {
        return benefit.benefitDisclaimer?.trim() || undefined;
      }
    }
  }

  return undefined;
};
export const resolveIconType = (icon: string): string => UXL_ICON_MAPPER[icon] ?? icon.replaceAll('_', '-');

export const getBenefits = (benefits: TierBenefit[]) => {
  return benefits?.slice(0, 3)?.map(item => {
    const iconType = item?.icon ? resolveIconType(item?.icon) : '';

    return {
      title: item?.title,
      description: item?.fullText,
      iconType,
    };
  });
};
